/**
 * ParametresView
 * Generic "Settings" sub-tab inside the Facturation tab.
 * Hosts read-only configuration panels — currently just province availability
 * per bank. New panels (TVA rules, sequences, etc.) plug in here over time.
 */

import React, { useMemo } from 'react';
import { GlobeEuropeAfricaIcon } from '@heroicons/react/24/outline';
import { useBanqueProvinces, useTarifs } from '../../hooks/useTarifs';
import { aggregateProvinces, provinceLabel } from '../../utils/provinces';
import '../../facturation.css';

const ProvincesPanel: React.FC = () => {
  const { data: banqueRules, isLoading: isLoadingRules } = useBanqueProvinces();
  // Reuse the existing tarif list to compute per-bank product counts; the list
  // endpoint caps at 200 rows so this counter is approximate when the catalogue
  // grows past that — fine for a read-only audit view.
  const { data: tarifs } = useTarifs({ activeOnly: true });

  const productCountByBank = useMemo(() => {
    const map = new Map<string, number>();
    (tarifs || []).forEach(t => {
      map.set(t.banque_code, (map.get(t.banque_code) || 0) + 1);
    });
    return map;
  }, [tarifs]);

  if (isLoadingRules) {
    return <div className="param-loading">Chargement…</div>;
  }

  if (!banqueRules || banqueRules.length === 0) {
    return (
      <div className="param-empty">
        Aucune règle banque trouvée. Importez un fichier Excel contenant la
        section <strong>DISPONIBILITÉ PAR PROVINCE</strong> pour peupler ce
        tableau.
      </div>
    );
  }

  return (
    <div className="param-panel">
      <div className="param-panel-header">
        <GlobeEuropeAfricaIcon className="param-panel-icon" />
        <div>
          <h3 className="param-panel-title">Disponibilité par province</h3>
          <p className="param-panel-subtitle">
            Règle par défaut par banque (ligne <em>RÈGLE BANQUE</em> de l'Excel).
            Les produits sans valeur explicite héritent de cette règle.
          </p>
        </div>
      </div>

      <div className="param-table-wrapper">
        <table className="param-table">
          <thead>
            <tr>
              <th>Banque</th>
              <th>Disponibilité (règle banque)</th>
              <th className="param-table-num">Produits actifs</th>
            </tr>
          </thead>
          <tbody>
            {banqueRules.map(rule => {
              const provs = aggregateProvinces(rule.available_provinces || []);
              const count = productCountByBank.get(rule.banque_code) || 0;
              return (
                <tr key={rule.banque_code}>
                  <td className="param-table-bank">
                    <span className="param-table-bank-name">{rule.banque_name}</span>
                    <span className="param-table-bank-code">{rule.banque_code}</span>
                  </td>
                  <td>
                    {provs.length === 0 ? (
                      <span className="param-no-coverage">Aucune province</span>
                    ) : (
                      <div className="pm-province-badges">
                        {provs.map(code => (
                          <span
                            key={code}
                            className={`pm-province-badge pm-province-badge--${code.toLowerCase()}`}
                            title={provinceLabel(code)}
                          >
                            {code}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="param-table-num">{count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="param-footnote">
        Lecture seule — pour modifier, ajustez l'Excel puis ré-importez depuis
        l'onglet <strong>Produits → Importer Excel</strong>.
      </p>
    </div>
  );
};

export const ParametresView: React.FC = () => {
  return (
    <div className="param-view">
      <ProvincesPanel />
    </div>
  );
};

export default ParametresView;
